import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { WTBCard } from './WTBCard';
import { CatalogCard } from './CatalogCard';
import { LocationAutocomplete } from './LocationAutocomplete';
import { useLocation } from '../context/LocationContext';
import { useAuth } from '../context/AuthContext';
import { getRecentlyViewed } from '../utils/recentlyViewed';
import { trackEvent } from '../utils/tracking';
import {
  Search,
  MapPin,
  ChevronDown,
  ChevronRight,
  Laptop,
  Sofa,
  Shirt,
  Car,
  Bike,
  Home,
  Trophy,
  BookOpen,
  Wrench,
  Package,
  Users,
  ShoppingBag,
  Sparkles,
  Navigation,
  Clock,
  RefreshCw,
  DollarSign,
  Tag,
  Eraser,
  MoreHorizontal,
  EyeOff,
} from 'lucide-react';
import { toast } from '../lib/toast';
import { useNewItemsPolling } from '../hooks/useNewItemsPolling';
import { useTileSize } from '../hooks/useTileSize';
import { TileSizeSlider } from './TileSizeSlider';
import { Button } from './ui/button';
import { Input } from './ui/input';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORY_ICONS = {
  electronics: Laptop,
  furniture: Sofa,
  clothing: Shirt,
  vehicles: Car,
  sports: Bike,
  home: Home,
  collectibles: Trophy,
  books: BookOpen,
  tools: Wrench,
  baby: Users,
  music: Sparkles,
  pets: Package,
  other: Package,
};

const RADIUS_OPTIONS = [2, 5, 10, 20, 30, 40, 50, 75, 100, 500];

const PRICE_PRESETS = [
  { label: 'Any', min: null, max: null },
  { label: 'Free', min: null, max: 0 },
  { label: 'Under $20', min: null, max: 20 },
  { label: 'Under $50', min: null, max: 50 },
  { label: 'Under $100', min: null, max: 100 },
  { label: 'Under $500', min: null, max: 500 },
  { label: '$500+', min: 500, max: null },
];

/**
 * Shared catalog view used by HomePage, PetsLandingPage, AutoLandingPage,
 * and all future vertical landing pages. Owns filter bar, state, fetch,
 * infinite scroll, and grid render. Vertical pages should configure via
 * props — never fork this file.
 *
 * See docs/plans/catalog_design_specification.md for the authoritative
 * props contract and the "never fork CatalogCard/CatalogView" rules.
 */
export function CatalogView({
  lockedCategory = null,
  lockedSubcategory = null,
  lockedSubcategoryOptions = null,
  linkPrefix = null,
  extraParams = null,
  heroSlot = null,
  bannerSlot = null,
  emptyStateSlot = null,
  showFeedModes = true,
  showCategoryDropdown = true,
  showViewToggle = true,
  showPriceDropdown = true,
  defaultSortBy = null,
  sectionTitle = null,
  itemNoun = 'items',
} = {}) {
  const { location, setManualLocation, requestLocation } = useLocation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [catalogItems, setCatalogItems] = useState([]);
  const [wtbListings, setWtbListings] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState(searchParams.get('q') || '');
  const [searchInput, setSearchInput] = useState(searchParams.get('q') || '');
  const [selectedCategory, setSelectedCategory] = useState(lockedCategory || searchParams.get('category') || 'all');
  const [selectedSubcategory, setSelectedSubcategory] = useState(lockedSubcategory || searchParams.get('sub') || null);
  const [radius, setRadius] = useState(50);
  const [locationInput, setLocationInput] = useState(location.name);
  const [recentlyViewed, setRecentlyViewed] = useState([]);

  // When category is locked, viewMode is forced to 'buy' (wants mode is main-catalog only)
  const [viewMode, setViewMode] = useState(() => {
    if (!showViewToggle) return 'buy';
    const viewParam = searchParams.get('view');
    if (viewParam === 'wants') return 'wants';
    return 'buy';
  });

  // Feed mode: 'nearby' | 'foryou' | 'recents' (only applies in Buy mode)
  const [feedMode, setFeedMode] = useState(searchParams.get('feed') || 'nearby');

  // Price filters
  const [minPrice, setMinPrice] = useState(searchParams.get('min') != null && searchParams.get('min') !== '' ? Number(searchParams.get('min')) : null);
  const [maxPrice, setMaxPrice] = useState(searchParams.get('max') != null && searchParams.get('max') !== '' ? Number(searchParams.get('max')) : null);

  // Live catalog polling — active only when feedMode === 'recents'
  const handleLoadNewItems = useCallback(() => {
    fetchData();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const { newCount, loadNewItems, resetCount } = useNewItemsPolling({
    feedMode: viewMode === 'buy' ? feedMode : 'off',
    onLoadNewItems: handleLoadNewItems,
  });

  // Tile size slider (persists across verticals via shared localStorage)
  const { tileSize, setTileSize, gridStyle, MIN_TILE_SIZE, MAX_TILE_SIZE } = useTileSize();

  // Infinite scroll state
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const sentinelRef = useRef(null);
  const observerRef = useRef(null);

  // Dropdown states
  const [showCategoryDropdownOpen, setShowCategoryDropdownOpen] = useState(false);
  const [showLocationDropdown, setShowLocationDropdown] = useState(false);
  const [showRadiusDropdown, setShowRadiusDropdown] = useState(false);
  const [showPriceDropdownOpen, setShowPriceDropdownOpen] = useState(false);
  const [suppressMenuItemId, setSuppressMenuItemId] = useState(null);

  const priceLabel = useMemo(() => {
    if (minPrice == null && maxPrice == null) return 'Price';
    if (maxPrice === 0) return 'Free';
    if (minPrice == null) return `Under $${maxPrice}`;
    if (maxPrice == null) return `$${minPrice}+`;
    return `$${minPrice}–$${maxPrice}`;
  }, [minPrice, maxPrice]);

  const hasActiveFilters = useMemo(() => {
    if (searchQuery) return true;
    if (!lockedCategory && selectedCategory !== 'all') return true;
    if (!lockedSubcategory && selectedSubcategory) return true;
    if (minPrice != null || maxPrice != null) return true;
    if (feedMode !== 'nearby') return true;
    if (radius !== 50) return true;
    return false;
  }, [searchQuery, selectedCategory, selectedSubcategory, minPrice, maxPrice, feedMode, radius, lockedCategory, lockedSubcategory]);

  const clearAllFilters = useCallback(() => {
    setSearchQuery('');
    setSearchInput('');
    if (!lockedCategory) setSelectedCategory('all');
    if (!lockedSubcategory) setSelectedSubcategory(null);
    setMinPrice(null);
    setMaxPrice(null);
    setFeedMode('nearby');
    setRadius(50);
    setShowRecent(false);
  }, [lockedCategory, lockedSubcategory]);

  // Recently viewed overlay
  const [showRecent, setShowRecent] = useState(false);

  // Serialize extraParams for useEffect dep key (objects change identity)
  const extraParamsKey = useMemo(() => JSON.stringify(extraParams || {}), [extraParams]);

  // Update view mode when URL parameter changes (main catalog only)
  useEffect(() => {
    if (!showViewToggle) return;
    const viewParam = searchParams.get('view');
    if (viewParam === 'wants') {
      setViewMode('wants');
    } else {
      setViewMode('buy');
    }
    setShowRecent(false);
  }, [searchParams, showViewToggle]);

  const toggleViewMode = () => {
    setViewMode(prev => (prev === 'wants' ? 'buy' : 'wants'));
    setShowRecent(false);
  };

  // Filtered recently viewed items based on current view mode
  const filteredRecent = recentlyViewed.filter(item =>
    viewMode === 'buy' ? item.type === 'inventory' : item.type === 'wtb'
  );

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (!e.target.closest('[data-dropdown]')) {
        setShowCategoryDropdownOpen(false);
        setShowLocationDropdown(false);
        setShowRadiusDropdown(false);
        setShowPriceDropdownOpen(false);
      }
      // Close For You card menu on any outside click
      setSuppressMenuItemId(null);
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  // Load recently viewed items (excluding user's own listings)
  useEffect(() => {
    const recent = getRecentlyViewed();
    const filtered = user ? recent.filter(item => item.user_id !== user.id) : recent;
    setRecentlyViewed(filtered);
  }, [user]);

  useEffect(() => {
    resetCount();
    fetchData();
  }, [location, selectedCategory, selectedSubcategory, radius, searchQuery, user?.id, viewMode, feedMode, minPrice, maxPrice, extraParamsKey]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setLocationInput(location.name);
  }, [location.name]);

  // Sync to locked props if a vertical page updates them (e.g. slug change)
  useEffect(() => {
    if (lockedCategory) setSelectedCategory(lockedCategory);
    if (lockedSubcategory !== null && lockedSubcategory !== undefined) {
      setSelectedSubcategory(lockedSubcategory);
    }
  }, [lockedCategory, lockedSubcategory]);

  // Hydrate all filter state from URL on external navigation
  // (breadcrumb links, shared URLs, browser back/forward — REFs 136, 146)
  // Same-value setState calls are no-ops in React so this won't cause infinite loops
  // with the sync effect below.
  useEffect(() => {
    if (lockedCategory) return; // vertical pages manage category via props
    setSelectedCategory(searchParams.get('category') || 'all');
    setSelectedSubcategory(searchParams.get('sub') || null);
    setFeedMode(searchParams.get('feed') || 'nearby');
    if (showViewToggle) setViewMode(searchParams.get('view') === 'wants' ? 'wants' : 'buy');
    const q = searchParams.get('q') || '';
    setSearchQuery(q);
    setSearchInput(q);
    const min = searchParams.get('min');
    const max = searchParams.get('max');
    setMinPrice(min !== null && min !== '' ? Number(min) : null);
    setMaxPrice(max !== null && max !== '' ? Number(max) : null);
  }, [searchParams, lockedCategory]); // eslint-disable-line react-hooks/exhaustive-deps

  // Sync filter state → URL so the browser back button restores filters (REF 146)
  // Uses replace:true so each filter tweak doesn't add a new history entry.
  useEffect(() => {
    if (lockedCategory) return; // vertical pages own their URL
    const params = new URLSearchParams();
    if (viewMode === 'wants') params.set('view', 'wants');
    if (selectedCategory && selectedCategory !== 'all') params.set('category', selectedCategory);
    if (selectedSubcategory) params.set('sub', selectedSubcategory);
    if (feedMode !== 'nearby') params.set('feed', feedMode);
    if (searchQuery) params.set('q', searchQuery);
    if (minPrice != null) params.set('min', String(minPrice));
    if (maxPrice != null) params.set('max', String(maxPrice));
    setSearchParams(params, { replace: true });
  }, [selectedCategory, selectedSubcategory, viewMode, feedMode, searchQuery, minPrice, maxPrice, lockedCategory, setSearchParams]);

  const getCatalogParams = useCallback(() => {
    const effectiveCategory = lockedCategory || (selectedCategory !== 'all' ? selectedCategory : undefined);
    const effectiveSubcategory = lockedSubcategory || selectedSubcategory || undefined;
    return {
      lat: location.lat,
      lng: location.lng,
      radius_km: radius,
      category: effectiveCategory,
      subcategory: effectiveSubcategory,
      search: searchQuery || undefined,
      feed_mode: feedMode,
      min_price: minPrice != null ? minPrice : undefined,
      max_price: maxPrice != null ? maxPrice : undefined,
      is_free: maxPrice === 0 ? true : undefined,
      sort_by: defaultSortBy || undefined,
      ...(extraParams || {}),
    };
  }, [location, radius, selectedCategory, selectedSubcategory, searchQuery, feedMode, minPrice, maxPrice, lockedCategory, lockedSubcategory, defaultSortBy, extraParams]);

  const fetchData = async () => {
    setLoading(true);
    setPage(0);
    setHasMore(true);
    try {
      const categoriesRes = await axios.get(`${API_URL}/api/categories`);
      setCategories(categoriesRes.data);

      if (viewMode === 'buy') {
        const headers = {};
        const token = localStorage.getItem('token');
        if (token) headers['Authorization'] = `Bearer ${token}`;

        const catalogRes = await axios.get(`${API_URL}/api/inventory/catalog/`, {
          params: { ...getCatalogParams(), skip: 0, limit: 20 },
          headers,
        });
        setCatalogItems(catalogRes.data);
        if (catalogRes.data.length < 20) setHasMore(false);
      } else {
        const listingsRes = await axios.get(`${API_URL}/api/wtb/`, {
          params: {
            lat: location.lat,
            lng: location.lng,
            radius_km: radius,
            category: lockedCategory || (selectedCategory !== 'all' ? selectedCategory : undefined),
            subcategory: lockedSubcategory || selectedSubcategory || undefined,
            search: searchQuery || undefined,
            exclude_user_id: user?.id || undefined,
            limit: 50,
          },
        });
        setWtbListings(listingsRes.data);
      }
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Infinite scroll — load more items
  const loadMore = useCallback(async () => {
    if (loadingMore || !hasMore || viewMode !== 'buy') return;
    setLoadingMore(true);
    const nextSkip = (page + 1) * 20;
    try {
      const headers = {};
      const token = localStorage.getItem('token');
      if (token) headers['Authorization'] = `Bearer ${token}`;

      const res = await axios.get(`${API_URL}/api/inventory/catalog/`, {
        params: { ...getCatalogParams(), skip: nextSkip, limit: 20 },
        headers,
      });
      if (res.data.length === 0) {
        setHasMore(false);
      } else {
        setCatalogItems(prev => [...prev, ...res.data]);
        setPage(prev => prev + 1);
        if (res.data.length < 20) setHasMore(false);
      }
    } catch (error) {
      console.error('Failed to load more:', error);
    } finally {
      setLoadingMore(false);
    }
  }, [loadingMore, hasMore, page, viewMode, getCatalogParams]);

  // IntersectionObserver for infinite scroll sentinel
  useEffect(() => {
    if (viewMode !== 'buy') return;
    if (observerRef.current) observerRef.current.disconnect();
    observerRef.current = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !loading) loadMore();
      },
      { rootMargin: '300px' }
    );
    if (sentinelRef.current) observerRef.current.observe(sentinelRef.current);
    return () => observerRef.current?.disconnect();
  }, [loadMore, viewMode, loading]);

  const handleSearchKeyDown = (e) => {
    if (e.key === 'Enter') {
      setSearchQuery(searchInput);
      if (searchInput.trim()) {
        trackEvent('search', {
          query: searchInput.trim(),
          category: lockedCategory || (selectedCategory !== 'all' ? selectedCategory : undefined),
          subcategory: lockedSubcategory || selectedSubcategory || undefined,
        });
      }
    }
  };

  const handleUseMyLocation = () => {
    requestLocation();
    setShowLocationDropdown(false);
  };

  const handleSuppress = async (item, durationDays) => {
    setSuppressMenuItemId(null);
    const label = categories.find(c => c.id === item.category)?.name || item.category;
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_URL}/api/users/suppress`,
        { category: item.category, subcategory: item.subcategory || null, label, duration_days: durationDays },
        { headers: token ? { Authorization: `Bearer ${token}` } : {} }
      );
      // Remove all items in this category from the current view immediately
      setCatalogItems(prev => prev.filter(i => i.category !== item.category));
      const msg = durationDays === null
        ? `${label} removed from your feed`
        : durationDays === 14
          ? `Hiding ${label} for 2 weeks`
          : durationDays === 30
            ? `Hiding ${label} for 1 month`
            : `Hiding ${label} for 3 months`;
      toast.success(msg);
    } catch {
      toast.error('Failed to update feed preferences');
    }
  };

  const currentItems = viewMode === 'buy' ? catalogItems : wtbListings;
  const itemCount = currentItems.length;

  const showCategoryDropdownControl = showCategoryDropdown && !lockedCategory && !lockedSubcategoryOptions;
  const showSubcategoryPills = lockedSubcategoryOptions && lockedSubcategoryOptions.length > 0;

  // ── For You card menu — inline component ────────────────────────────────
  // Renders a small ⋯ button overlaid on the top-right corner of each For You
  // card. On click shows a dropdown with snooze/suppress options.
  const ForYouMenu = ({ item, open, onOpen, onClose }) => {
    const catLabel = categories.find(c => c.id === item.category)?.name || item.category;
    return (
      <div className="absolute top-1.5 right-1.5 z-10">
        <button
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); open ? onClose() : onOpen(); }}
          className="w-6 h-6 flex items-center justify-center rounded-full bg-black/40 hover:bg-black/60 text-white transition-colors"
          title="Feed options"
          aria-label="Feed options"
        >
          <MoreHorizontal className="w-3.5 h-3.5" />
        </button>
        {open && (
          <div
            className="absolute right-0 top-7 w-52 bg-[var(--theme-card)] border border-[var(--theme-border)] rounded-lg shadow-xl py-1 text-sm"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-3 py-1.5 text-xs font-semibold text-[var(--theme-text-muted)] uppercase tracking-wide flex items-center gap-1.5">
              <EyeOff className="w-3 h-3" /> Hide {catLabel}
            </div>
            {[
              { label: 'Snooze for 2 weeks', days: 14 },
              { label: 'Snooze for 1 month', days: 30 },
              { label: 'Snooze for 3 months', days: 90 },
              { label: `Don't show me ${catLabel}`, days: null },
            ].map(({ label, days }) => (
              <button
                key={label}
                onClick={() => handleSuppress(item, days)}
                className="w-full text-left px-3 py-2 hover:bg-[var(--theme-hover)] text-[var(--theme-text-primary)] transition-colors"
              >
                {label}
              </button>
            ))}
          </div>
        )}
      </div>
    );
  };

  // Default section title if vertical didn't supply one
  const effectiveSectionTitle = sectionTitle || (
    showRecent
      ? 'Recently Viewed'
      : viewMode === 'buy'
        ? (feedMode === 'foryou' ? 'For You' : feedMode === 'recents' ? 'New Listings' : 'Items Near You')
        : 'What People Want Near You'
  );

  return (
    <div className="w-full">
      {/* Hero slot — vertical-specific */}
      {heroSlot}

      {/* Sticky Filter Bar */}
      <div className="sticky top-[60px] md:top-[64px] z-40 bg-[var(--theme-catalog-bar)] -mt-px">
        {/* ───────── Row 1: Search + Location + Radius ───────── */}
        <div className="flex items-center gap-2 px-3 md:px-4 py-2">
          <div className="relative flex-1 min-w-0">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[var(--theme-text-muted)]" />
            <Input
              type="text"
              placeholder={viewMode === 'buy' ? `Search ${itemNoun} for sale...` : 'Search wants...'}
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={handleSearchKeyDown}
              className="pl-8 h-9 bg-[var(--theme-card)] border-[var(--theme-border)] text-[var(--theme-text-primary)] rounded-lg text-sm placeholder:text-[var(--theme-text-muted)] w-full"
              data-testid="search-input"
            />
          </div>

          {/* Location Dropdown */}
          <div className="relative flex-shrink-0" data-dropdown>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setShowLocationDropdown(!showLocationDropdown);
                setShowCategoryDropdownOpen(false);
                setShowRadiusDropdown(false);
                setShowPriceDropdownOpen(false);
              }}
              className="flex items-center gap-1.5 h-9 px-2.5 bg-[var(--theme-card)] border border-[var(--theme-border)] rounded-lg hover:border-[var(--theme-border-hover)] transition-colors"
              data-testid="location-dropdown-btn"
            >
              <MapPin className="w-3.5 h-3.5 text-[#C9956C]" />
              <span className="text-sm text-[var(--theme-text-primary)] truncate max-w-[80px] hidden sm:inline">{location.name}</span>
              <ChevronDown className={`w-3 h-3 text-[var(--theme-text-secondary)] transition-transform ${showLocationDropdown ? 'rotate-180' : ''}`} />
            </button>

            {showLocationDropdown && (
              <div className="absolute top-full right-0 mt-1 bg-[var(--theme-card)] border border-[var(--theme-border)] rounded-lg shadow-lg z-50 p-2 min-w-[220px]">
                <LocationAutocomplete
                  value={locationInput}
                  onChange={(val) => setLocationInput(val)}
                  onSelect={(locationData) => {
                    setManualLocation(locationData.lat, locationData.lng, locationData.name);
                    setShowLocationDropdown(false);
                  }}
                  placeholder="Enter suburb or postcode..."
                  className="mb-2"
                />
                <button
                  type="button"
                  onClick={() => { handleUseMyLocation(); setShowLocationDropdown(false); }}
                  className="w-full flex items-center gap-2 px-2 py-1.5 rounded hover:bg-[var(--theme-hover)] text-xs text-[var(--theme-text-primary)] transition-colors"
                >
                  <Navigation className="w-3 h-3 text-[#C9956C]" />
                  <span>Use my location</span>
                </button>
              </div>
            )}
          </div>

          {/* Radius Dropdown */}
          <div className="relative flex-shrink-0" data-dropdown>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setShowRadiusDropdown(!showRadiusDropdown);
                setShowLocationDropdown(false);
                setShowCategoryDropdownOpen(false);
                setShowPriceDropdownOpen(false);
              }}
              className="flex items-center gap-1 h-9 px-2.5 bg-[var(--theme-card)] border border-[var(--theme-border)] rounded-lg hover:border-[var(--theme-border-hover)] transition-colors"
              data-testid="radius-dropdown-btn"
            >
              <span className="text-sm text-[var(--theme-text-primary)]">{radius}km</span>
              <ChevronDown className={`w-3 h-3 text-[var(--theme-text-secondary)] transition-transform ${showRadiusDropdown ? 'rotate-180' : ''}`} />
            </button>

            {showRadiusDropdown && (
              <div className="absolute top-full right-0 mt-1 bg-[var(--theme-card)] border border-[var(--theme-border)] rounded-lg shadow-lg z-50 py-1 min-w-[90px]">
                {RADIUS_OPTIONS.map((r) => (
                  <button
                    key={r}
                    onClick={() => { setRadius(r); setShowRadiusDropdown(false); }}
                    className={`w-full text-left px-3 py-1 text-sm hover:bg-[var(--theme-hover)] transition-colors ${
                      radius === r ? 'text-[#C9956C] font-semibold bg-[var(--theme-hover)]' : 'text-[var(--theme-text-primary)]'
                    }`}
                  >
                    {r} km
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* ───────── Row 2: Buy/Want + Feed mode + Free + Recent + Categories + Price ───────── */}
        <div className="flex border-t border-white/10">
          {/* Scrollable left section */}
          <div className="flex-1 min-w-0 overflow-x-auto hide-scrollbar">
            <div className="flex items-center gap-2 px-3 md:px-4 py-2 min-w-max">
              {/* Buy/Want toggle */}
              {showViewToggle && (
                <button
                  onClick={toggleViewMode}
                  className={`flex-shrink-0 w-9 h-9 rounded-lg flex items-center justify-center transition-all duration-300 ${
                    viewMode === 'buy'
                      ? 'bg-white border border-gray-200'
                      : 'bg-[#1E3A5F] border border-white/30'
                  }`}
                  data-testid="view-mode-toggle"
                  title={viewMode === 'buy' ? 'Viewing Buy Catalog - Click for Wants' : 'Viewing Wants - Click for Buy Catalog'}
                >
                  <img
                    src={viewMode === 'buy' ? '/n-toggle-buy.png' : '/n-toggle-wants.png'}
                    alt={viewMode === 'wants' ? 'Wants' : 'Buy'}
                    className="w-5 h-5 object-contain"
                  />
                </button>
              )}

              {/* Feed Mode Toggle */}
              {showFeedModes && viewMode === 'buy' && (
                <div className="flex-shrink-0 flex rounded-lg overflow-hidden border border-white/30 h-9" data-testid="feed-mode-toggle">
                  <button
                    onClick={() => { setFeedMode('foryou'); setShowRecent(false); }}
                    className={`px-2.5 text-xs font-medium transition-all duration-200 ${
                      feedMode === 'foryou' ? 'bg-white text-[#1E3A5F]' : 'bg-transparent text-white/70 hover:text-white hover:bg-white/10'
                    }`}
                    data-testid="feed-foryou-btn"
                  >
                    For You
                  </button>
                  <button
                    onClick={() => { setFeedMode('nearby'); setShowRecent(false); }}
                    className={`px-2.5 text-xs font-medium transition-all duration-200 ${
                      feedMode === 'nearby' ? 'bg-white text-[#1E3A5F]' : 'bg-transparent text-white/70 hover:text-white hover:bg-white/10'
                    }`}
                    data-testid="feed-nearby-btn"
                  >
                    Nearby
                  </button>
                  <button
                    onClick={() => { setFeedMode('recents'); setShowRecent(false); }}
                    className={`px-2.5 text-xs font-medium transition-all duration-200 ${
                      feedMode === 'recents' ? 'bg-white text-[#1E3A5F]' : 'bg-transparent text-white/70 hover:text-white hover:bg-white/10'
                    }`}
                    data-testid="feed-recents-btn"
                  >
                    New
                  </button>
                </div>
              )}

              {/* Locked subcategory pills (replaces category dropdown for Pets/Auto verticals) */}
              {showSubcategoryPills && (
                <div className="flex-shrink-0 flex items-center gap-1.5" data-testid="subcategory-pills">
                  {lockedSubcategoryOptions.map((opt) => {
                    const active = (opt.id === null && !selectedSubcategory) || selectedSubcategory === opt.id;
                    return (
                      <button
                        key={opt.id || 'all'}
                        onClick={() => setSelectedSubcategory(opt.id)}
                        className={`px-3 h-9 rounded-lg text-xs font-medium border transition-all duration-200 whitespace-nowrap ${
                          active
                            ? 'bg-white border-white text-[#1E3A5F]'
                            : 'bg-transparent border-white/30 text-white/70 hover:text-white hover:border-white'
                        }`}
                        data-testid={`subcategory-pill-${opt.id || 'all'}`}
                      >
                        {opt.label}
                      </button>
                    );
                  })}
                </div>
              )}

              {/* Recently Viewed Toggle */}
              {filteredRecent.length > 0 && (
                <button
                  onClick={() => setShowRecent(prev => !prev)}
                  className={`flex-shrink-0 w-9 h-9 rounded-lg flex items-center justify-center transition-all duration-300 ${
                    showRecent
                      ? 'bg-[#1E3A5F] border border-white/30'
                      : 'bg-white border border-gray-200'
                  }`}
                  title={showRecent ? 'Back to catalog' : 'Recently Viewed'}
                  data-testid="recent-view-toggle"
                >
                  <Clock className={`w-4 h-4 ${showRecent ? 'text-white' : 'text-[#1E3A5F]'}`} />
                </button>
              )}
            </div>
          </div>

          {/* Right section (row 2) — dropdowns sit outside overflow so panels aren't clipped */}
          <div className="flex items-center gap-2 ml-2 md:ml-3 pr-3 md:pr-4 py-2 flex-shrink-0">

            {/* Category Dropdown */}
            {showCategoryDropdownControl && (
              <div className="relative" data-dropdown>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowCategoryDropdownOpen(!showCategoryDropdownOpen);
                    setShowLocationDropdown(false);
                    setShowRadiusDropdown(false);
                    setShowPriceDropdownOpen(false);
                  }}
                  className="flex items-center gap-1.5 h-9 px-3 bg-[var(--theme-card)] border border-[var(--theme-border)] rounded-lg hover:border-[var(--theme-border-hover)] transition-colors"
                  data-testid="category-dropdown-btn"
                >
                  <Package className="w-3.5 h-3.5 text-[var(--theme-text-muted)]" />
                  <span className="text-sm text-[var(--theme-text-primary)] truncate max-w-[100px]">
                    {selectedSubcategory
                      ? categories.find(c => c.id === selectedCategory)?.subcategories?.find(s => s.id === selectedSubcategory)?.name
                      : selectedCategory === 'all'
                        ? 'All'
                        : categories.find(c => c.id === selectedCategory)?.name || 'All'}
                  </span>
                  <ChevronDown className={`w-3.5 h-3.5 text-[var(--theme-text-secondary)] transition-transform ${showCategoryDropdownOpen ? 'rotate-180' : ''}`} />
                </button>

                {showCategoryDropdownOpen && (
                  <div className="absolute top-full right-0 mt-1 bg-[var(--theme-card)] border border-[var(--theme-border)] rounded-lg shadow-lg z-50 py-1 min-w-[200px] max-h-[350px] overflow-y-auto">
                    <button
                      onClick={() => { setSelectedCategory('all'); setSelectedSubcategory(null); setShowCategoryDropdownOpen(false); }}
                      className={`w-full text-left px-3 py-1.5 text-sm hover:bg-[var(--theme-hover)] transition-colors flex items-center gap-2 ${
                        selectedCategory === 'all' ? 'text-[#C9956C] font-semibold bg-[var(--theme-hover)]' : 'text-[var(--theme-text-primary)]'
                      }`}
                    >
                      <Package className="w-3.5 h-3.5" />
                      All Categories
                    </button>
                    {categories.map((cat) => {
                      const Icon = CATEGORY_ICONS[cat.id] || Package;
                      const hasSubcategories = cat.subcategories && cat.subcategories.length > 0;
                      return (
                        <div key={cat.id}>
                          <button
                            onClick={() => {
                              setSelectedCategory(cat.id);
                              setSelectedSubcategory(null);
                              if (!hasSubcategories) setShowCategoryDropdownOpen(false);
                            }}
                            className={`w-full text-left px-3 py-1.5 text-sm hover:bg-[var(--theme-hover)] transition-colors flex items-center gap-2 ${
                              selectedCategory === cat.id && !selectedSubcategory ? 'text-[#C9956C] font-semibold bg-[var(--theme-hover)]' : 'text-[var(--theme-text-primary)]'
                            }`}
                          >
                            <Icon className="w-3.5 h-3.5" />
                            <span className="flex-1">{cat.name}</span>
                            {hasSubcategories && <ChevronRight className="w-3 h-3" />}
                          </button>
                          {hasSubcategories && selectedCategory === cat.id && (
                            <div className="pl-6 bg-[var(--theme-hover)]/50">
                              {cat.subcategories.map(sub => (
                                <button
                                  key={sub.id}
                                  onClick={() => { setSelectedSubcategory(sub.id); setShowCategoryDropdownOpen(false); }}
                                  className={`w-full text-left px-3 py-1 text-xs hover:bg-[var(--theme-hover)] transition-colors ${
                                    selectedSubcategory === sub.id ? 'text-[#C9956C] font-semibold' : 'text-[var(--theme-text-secondary)]'
                                  }`}
                                >
                                  {sub.name}
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {/* Price Range Dropdown */}
            {showPriceDropdown && (
              <div className="relative" data-dropdown>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowPriceDropdownOpen(!showPriceDropdownOpen);
                    setShowCategoryDropdownOpen(false);
                    setShowLocationDropdown(false);
                    setShowRadiusDropdown(false);
                  }}
                  className={`flex items-center gap-1.5 h-9 px-3 bg-[var(--theme-card)] border rounded-lg transition-colors ${
                    (minPrice != null || maxPrice != null)
                      ? 'border-[#C9956C] text-[#C9956C]'
                      : 'border-[var(--theme-border)] hover:border-[var(--theme-border-hover)] text-[var(--theme-text-primary)]'
                  }`}
                  data-testid="price-dropdown-btn"
                >
                  <DollarSign className="w-3.5 h-3.5" />
                  <span className="text-sm truncate max-w-[100px]">{priceLabel}</span>
                  <ChevronDown className={`w-3.5 h-3.5 text-[var(--theme-text-secondary)] transition-transform ${showPriceDropdownOpen ? 'rotate-180' : ''}`} />
                </button>

                {showPriceDropdownOpen && (
                  <div className="absolute top-full right-0 mt-1 bg-[var(--theme-card)] border border-[var(--theme-border)] rounded-lg shadow-lg z-50 p-3 min-w-[260px]">
                    <div className="flex flex-wrap gap-1.5 mb-3">
                      {PRICE_PRESETS.map((preset) => {
                        const active = minPrice === preset.min && maxPrice === preset.max;
                        return (
                          <button
                            key={preset.label}
                            onClick={() => {
                              setMinPrice(preset.min);
                              setMaxPrice(preset.max);
                              setShowPriceDropdownOpen(false);
                            }}
                            className={`px-2.5 py-1 text-xs rounded-md border transition-colors ${
                              active
                                ? 'bg-[#C9956C] border-[#C9956C] text-white font-semibold'
                                : 'bg-transparent border-[var(--theme-border)] text-[var(--theme-text-primary)] hover:border-[var(--theme-border-hover)]'
                            }`}
                          >
                            {preset.label}
                          </button>
                        );
                      })}
                    </div>
                    <div className="flex items-center gap-2">
                      <Input
                        type="number"
                        min="0"
                        placeholder="Min"
                        value={minPrice ?? ''}
                        onChange={(e) => setMinPrice(e.target.value === '' ? null : Number(e.target.value))}
                        className="h-8 text-xs"
                      />
                      <span className="text-xs text-[var(--theme-text-muted)]">to</span>
                      <Input
                        type="number"
                        min="0"
                        placeholder="Max"
                        value={maxPrice ?? ''}
                        onChange={(e) => setMaxPrice(e.target.value === '' ? null : Number(e.target.value))}
                        className="h-8 text-xs"
                      />
                    </div>
                    {(minPrice != null || maxPrice != null) && (
                      <button
                        onClick={() => { setMinPrice(null); setMaxPrice(null); setShowPriceDropdownOpen(false); }}
                        className="mt-2 w-full text-xs text-[var(--theme-text-muted)] hover:text-[var(--theme-text-primary)] transition-colors"
                      >
                        Clear price filter
                      </button>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Clear all filters — only visible when at least one filter is active */}
            {hasActiveFilters && (
              <button
                type="button"
                onClick={clearAllFilters}
                className="flex items-center gap-1.5 h-9 px-3 rounded-lg border border-white/30 bg-transparent text-white/80 hover:text-white hover:border-white hover:bg-white/10 transition-colors"
                title="Clear all filters"
                data-testid="clear-all-filters-btn"
              >
                <Eraser className="w-3.5 h-3.5" />
                <span className="text-xs font-medium">Clear all</span>
              </button>
            )}

          </div>{/* /row 2 right dropdowns section */}
        </div>{/* /row 2 */}
      </div>{/* /sticky filter bar */}

      {/* Banner slot — vertical-specific (e.g. T&Cs) */}
      {bannerSlot}

      {/* Live New Items Banner — recents feed only */}
      {newCount > 0 && viewMode === 'buy' && feedMode === 'recents' && (
        <div className="mx-3 md:mx-4 mt-2 animate-slide-down" style={{ animation: 'slideDown 0.25s ease-out' }}>
          <button
            onClick={loadNewItems}
            className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-[#1E3A5F] text-white text-sm font-semibold rounded-xl shadow-[0_4px_16px_rgba(30,58,95,0.35)] hover:bg-[#2D4A6F] active:scale-[0.98] transition-all duration-150"
            data-testid="new-items-banner"
          >
            <RefreshCw className="w-4 h-4" />
            {newCount === 1 ? '1 new item' : `${newCount} new items`} — Tap to see
          </button>
        </div>
      )}

      {/* Main Content */}
      <div className="px-3 md:px-4 py-3">
        {/* Section Header */}
        <div className="flex items-center justify-between mb-3">
          <div>
            <h2 className="text-lg font-bold text-[var(--theme-text-primary)]">
              {effectiveSectionTitle}
            </h2>
            {showRecent ? (
              <p className="text-xs text-[var(--theme-text-secondary)]">
                <strong className="text-[var(--theme-text-primary)]">{filteredRecent.length}</strong> {viewMode === 'buy' ? itemNoun : 'wants'} you've browsed
              </p>
            ) : (
              <p className="text-xs text-[var(--theme-text-secondary)]">
                <strong className="text-[var(--theme-text-primary)]">{itemCount}</strong> {viewMode === 'buy' ? itemNoun : 'wants'} within {radius} km
              </p>
            )}
          </div>

          <div className="flex items-center gap-3">
            <TileSizeSlider tileSize={tileSize} setTileSize={setTileSize} min={MIN_TILE_SIZE} max={MAX_TILE_SIZE} />
            <div className="flex items-center gap-1.5 text-xs text-[var(--theme-text-muted)]">
              <span className="hidden sm:inline">Viewing:</span>
              <span className={`px-2 py-0.5 rounded-full font-medium ${
                viewMode === 'buy' ? 'bg-[#1E3A5F] text-white' : 'bg-[#C9956C] text-white'
              }`}>
                {showRecent ? 'Recent' : viewMode === 'buy' ? 'Buy' : 'Wants'}
              </span>
            </div>
          </div>
        </div>

        {/* Content Grid */}
        {showRecent ? (
          <div style={gridStyle} data-testid="recent-grid">
            {filteredRecent.map((item) => {
              if (item.type === 'inventory') {
                return <CatalogCard key={item.id} item={item} linkPrefix={linkPrefix} />;
              }
              return <WTBCard key={item.id} listing={item} onClick={() => navigate(`/wtb/${item.id}`)} />;
            })}
          </div>
        ) : viewMode === 'buy' ? (
          <>
            {loading ? (
              <div style={gridStyle}>
                {[...Array(12)].map((_, i) => (
                  <div key={i} className="animate-pulse bg-[var(--theme-card)] rounded-xl overflow-hidden border border-[var(--theme-border)]">
                    <div className="aspect-square bg-[var(--theme-hover)]" />
                    <div className="p-2 space-y-1.5">
                      <div className="h-3 bg-[var(--theme-hover)] rounded w-1/2" />
                      <div className="h-2.5 bg-[var(--theme-hover)] rounded w-3/4" />
                    </div>
                  </div>
                ))}
              </div>
            ) : catalogItems.length === 0 ? (
              emptyStateSlot || (
                <div className="text-center py-12 bg-[var(--theme-card)] rounded-xl border border-[var(--theme-border)]">
                  <Search className="w-10 h-10 text-[var(--theme-text-muted)] mx-auto mb-3" />
                  <h3 className="text-base font-semibold text-[var(--theme-text-primary)] mb-1">No {itemNoun} found</h3>
                  <p className="text-sm text-[var(--theme-text-secondary)] mb-3">
                    {feedMode === 'foryou' && !searchQuery
                      ? 'Create a Want to personalize your feed'
                      : feedMode === 'recents' && !searchQuery
                        ? 'No recent listings in this area'
                        : searchQuery ? `No results for "${searchQuery}"` : `No ${itemNoun} in this category yet`}
                  </p>
                  {user && feedMode === 'foryou' && !searchQuery && feedMode !== 'recents' ? (
                    <Link to="/create-wtb">
                      <Button size="sm" className="bg-[#C9956C] hover:bg-[#B8845B] text-white">
                        <ShoppingBag className="w-3.5 h-3.5 mr-1.5" />
                        Create a Want
                      </Button>
                    </Link>
                  ) : user && (
                    <Link to={
                      lockedCategory === 'vehicles' ? '/auto/cars/list'
                      : lockedCategory === 'live_animals' ? '/pets/list'
                      : '/list-item'
                    }>
                      <Button size="sm" className="bg-[#059669] hover:bg-[#047857] text-white">
                        <Tag className="w-3.5 h-3.5 mr-1.5" />
                        Be the first to {lockedCategory === 'vehicles' ? 'list' : lockedCategory === 'live_animals' ? 'rehome' : 'sell'}
                      </Button>
                    </Link>
                  )}
                </div>
              )
            ) : (
              <>
                <div style={gridStyle} data-testid="catalog-grid">
                  {catalogItems.map((item) => (
                    <div key={item.id} className="relative">
                      <CatalogCard item={item} linkPrefix={linkPrefix} />
                      {feedMode === 'foryou' && user && (
                        <ForYouMenu
                          item={item}
                          open={suppressMenuItemId === item.id}
                          onOpen={() => setSuppressMenuItemId(item.id)}
                          onClose={() => setSuppressMenuItemId(null)}
                        />
                      )}
                    </div>
                  ))}
                </div>

                <div ref={sentinelRef} className="h-1" />
                {loadingMore && (
                  <div className="flex justify-center py-6">
                    <div className="w-6 h-6 border-2 border-[#1E3A5F] border-t-transparent rounded-full animate-spin" />
                  </div>
                )}
                {!hasMore && catalogItems.length > 0 && (
                  <p className="text-center text-xs text-[var(--theme-text-muted)] py-4">
                    You've seen all {catalogItems.length} {itemNoun}
                  </p>
                )}
              </>
            )}
          </>
        ) : (
          <>
            {loading ? (
              <div style={gridStyle}>
                {[...Array(8)].map((_, i) => (
                  <div key={i} className="animate-pulse bg-[var(--theme-card)] rounded-xl overflow-hidden border border-[var(--theme-border)]">
                    <div className="aspect-square bg-[var(--theme-hover)]" />
                    <div className="p-2 space-y-1.5">
                      <div className="h-3 bg-[var(--theme-hover)] rounded w-1/2" />
                      <div className="h-2.5 bg-[var(--theme-hover)] rounded w-3/4" />
                    </div>
                  </div>
                ))}
              </div>
            ) : wtbListings.length === 0 ? (
              <div className="text-center py-12 bg-[var(--theme-card)] rounded-xl border border-[var(--theme-border)]">
                <ShoppingBag className="w-10 h-10 text-[var(--theme-text-muted)] mx-auto mb-3" />
                <h3 className="text-base font-semibold text-[var(--theme-text-primary)] mb-1">No wants found</h3>
                <p className="text-sm text-[var(--theme-text-secondary)] mb-3">No one is looking for items in this area yet</p>
                {user && (
                  <Link to="/create-wtb">
                    <Button size="sm" className="bg-[#C9956C] hover:bg-[#B8845B] text-white">
                      <ShoppingBag className="w-3.5 h-3.5 mr-1.5" />
                      Post what you want
                    </Button>
                  </Link>
                )}
              </div>
            ) : (
              <div style={gridStyle} data-testid="wtb-grid">
                {wtbListings.map((listing) => (
                  <WTBCard key={listing.id} listing={listing} onClick={() => navigate(`/wtb/${listing.id}`)} />
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default CatalogView;
